import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { formatPlayCount } from "@/utils/video";

export interface HeroSlide {
  id: string;
  title: string;
  cover: string;
  categoryName: string;
  description: string;
  playCount: number;
}

interface HeroCarouselProps {
  slides: HeroSlide[];
}

export default function HeroCarousel({ slides }: HeroCarouselProps) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  if (slides.length === 0) return null;

  const go = (dir: number) => {
    setActive((prev) => (prev + dir + slides.length) % slides.length);
  };

  return (
    <section className="relative h-[380px] w-full overflow-hidden rounded-2xl bg-foreground-950 md:h-[540px]">
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === active ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <img
            src={slide.cover}
            alt={`${slide.title} 焦点推荐`}
            title={`${slide.title} - 色色视频`}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/40 to-foreground-950/20"></div>
        </div>
      ))}

      <div className="relative z-10 flex h-full w-full flex-col justify-end p-6 md:p-10">
        <span className="mb-3 inline-flex w-fit items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
          <i className="ri-fire-fill" aria-hidden="true"></i>
          今日焦点
        </span>
        <h2 className="font-heading text-3xl leading-tight text-background-50 md:text-5xl">
          {slides[active].title}
        </h2>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">
          {slides[active].description}
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <Link
            to={`/detail/${slides[active].id}`}
            className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-play-fill text-lg" aria-hidden="true"></i>
            立即观看
          </Link>
          <span className="flex items-center gap-1.5 text-sm text-background-200">
            <i className="ri-play-circle-line text-base" aria-hidden="true"></i>
            {formatPlayCount(slides[active].playCount)} 次播放
          </span>
          <span className="rounded-md bg-background-50/15 px-2.5 py-1 text-xs text-background-100">
            {slides[active].categoryName}
          </span>
        </div>
      </div>

      <div className="absolute bottom-6 right-6 z-10 hidden items-center gap-2 md:flex">
        <button
          type="button"
          onClick={() => go(-1)}
          aria-label="上一张"
          className="flex h-10 w-10 items-center justify-center rounded-full bg-background-50/20 text-background-50 transition-colors hover:bg-background-50/35"
        >
          <i className="ri-arrow-left-s-line text-2xl" aria-hidden="true"></i>
        </button>
        <button
          type="button"
          onClick={() => go(1)}
          aria-label="下一张"
          className="flex h-10 w-10 items-center justify-center rounded-full bg-background-50/20 text-background-50 transition-colors hover:bg-background-50/35"
        >
          <i className="ri-arrow-right-s-line text-2xl" aria-hidden="true"></i>
        </button>
      </div>

      <div className="absolute bottom-6 left-6 z-10 flex items-center gap-2 md:left-10">
        {slides.map((slide, index) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => setActive(index)}
            aria-label={`切换到 ${slide.title}`}
            className={`h-1.5 rounded-full transition-all ${
              index === active ? "w-8 bg-primary-500" : "w-4 bg-background-50/50 hover:bg-background-50/80"
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}